import { getZegoToken } from "../services/api/zego.api";

export interface TokenManagerOptions {
  userId: string;
  tokenPrefix?: string;
  expiryHours?: number;
}

export interface TokenResult {
  token: string | null;
  isFromCache: boolean;
}

/**
 * Manages Zego tokens with localStorage caching and automatic expiry
 * @param options Configuration options for token management
 * @returns Promise<TokenResult> - Contains token and cache status
 */
export const getOrFetchZegoToken = async (
  options: TokenManagerOptions,
): Promise<TokenResult> => {
  const { userId, tokenPrefix = "zegoToken", expiryHours = 1 } = options;

  if (!userId) {
    throw new Error("userId is required");
  }

  const tokenKey = `${tokenPrefix}-${userId}`;
  const tokenExpiryKey = `${tokenPrefix}Expiry-${userId}`;

  // Check for existing token in localStorage
  const storedToken = localStorage.getItem(tokenKey);
  const storedExpiry = localStorage.getItem(tokenExpiryKey);

  // Validate existing token
  if (storedToken && storedExpiry) {
    const expiryTime = parseInt(storedExpiry);
    const currentTime = Date.now();

    if (currentTime < expiryTime) {
      // Token is still valid
      return {
        token: storedToken,
        isFromCache: true,
      };
    } else {
      // Token is expired, remove it
      localStorage.removeItem(tokenKey);
      localStorage.removeItem(tokenExpiryKey);
      console.log(`Token expired and removed: ${tokenKey}`);
    }
  }

  try {
    // Fetch new token from API
    console.log(`Fetching new token for userId: ${userId}`);
    const newToken = await getZegoToken(userId);

    // Store token with expiry
    const expiryTime = Date.now() + expiryHours * 60 * 60 * 1000;
    localStorage.setItem(tokenKey, newToken);
    localStorage.setItem(tokenExpiryKey, expiryTime.toString());

    console.log(`New token stored with ${expiryHours}h expiry: ${tokenKey}`);

    return {
      token: newToken,
      isFromCache: false,
    };
  } catch (error) {
    console.error(`Failed to fetch token for ${userId}:`, error);
    throw error;
  }
};

/**
 * Get message token specifically for chat functionality
 * @param userId User identifier
 * @returns Promise<TokenResult> - Message token result
 */
export const getMessageToken = async (userId: string): Promise<TokenResult> => {
  return getOrFetchZegoToken({
    userId,
    tokenPrefix: "messageToken",
    expiryHours: 1,
  });
};

/**
 * Get video token specifically for live streaming
 * @param userId User identifier
 * @returns Promise<TokenResult> - Video token result
 */
export const getVideoToken = async (userId: string): Promise<TokenResult> => {
  return getOrFetchZegoToken({
    userId,
    tokenPrefix: "videoToken",
    expiryHours: 2, // Video tokens might need longer expiry
  });
};

/**
 * Clear all tokens for a specific user
 * @param userId User identifier
 * @param tokenPrefixes Array of token prefixes to clear (optional)
 */
export const clearUserTokens = (
  userId: string,
  tokenPrefixes: string[] = ["messageToken", "videoToken"],
): void => {
  tokenPrefixes.forEach((prefix) => {
    const tokenKey = `${prefix}-${userId}`;
    const expiryKey = `${prefix}Expiry-${userId}`;

    localStorage.removeItem(tokenKey);
    localStorage.removeItem(expiryKey);

    console.log(`Cleared tokens: ${tokenKey}`);
  });
};

/**
 * Check if a token exists and is valid without fetching a new one
 * @param userId User identifier
 * @param tokenPrefix Token prefix (default: "zegoToken")
 * @returns boolean - True if valid token exists
 */
export const hasValidToken = (
  userId: string,
  tokenPrefix: string = "zegoToken",
): boolean => {
  if (!userId) return false;

  const tokenKey = `${tokenPrefix}-${userId}`;
  const expiryKey = `${tokenPrefix}Expiry-${userId}`;

  const storedToken = localStorage.getItem(tokenKey);
  const storedExpiry = localStorage.getItem(expiryKey);

  if (!storedToken || !storedExpiry) return false;

  const expiryTime = parseInt(storedExpiry);
  const currentTime = Date.now();

  return currentTime < expiryTime;
};

/**
 * Get remaining time for a token in minutes
 * @param userId User identifier
 * @param tokenPrefix Token prefix (default: "zegoToken")
 * @returns number - Remaining minutes (0 if expired or not found)
 */
export const getTokenRemainingTime = (
  userId: string,
  tokenPrefix: string = "zegoToken",
): number => {
  if (!userId) return 0;

  const expiryKey = `${tokenPrefix}Expiry-${userId}`;
  const storedExpiry = localStorage.getItem(expiryKey);

  if (!storedExpiry) return 0;

  const expiryTime = parseInt(storedExpiry);
  const currentTime = Date.now();
  const remainingMs = Math.max(0, expiryTime - currentTime);

  return Math.floor(remainingMs / (60 * 1000)); // Convert to minutes
};
